/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import React from 'react';
import { MyPageTicket } from './types';

export type TicketDetailPageProps = {
  ticket: MyPageTicket | null;
  primaryColor: string;
};

export const TicketDetailPage: React.FC<TicketDetailPageProps> = ({
  ticket,
  primaryColor,
}) => {
  if (!ticket) {
    return (
      <div css={styles.container}>
        <div css={styles.emptyState}>チケットが選択されていません</div>
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.ticketTypeSection}>
        <span css={styles.ticketType(primaryColor)}>{ticket.ticketType}</span>
      </div>
      {/* チケットカード */}
      <div css={styles.ticketCard}>
        {/* 有効期限 */}
        <div css={styles.ticketInfoSection}>
          <span css={styles.expiryDate}>
            <span css={styles.expiryDateText}>{ticket.expireDate}まで有効</span>
            <CheckCircleOutlineIcon css={styles.expiryDateSuccess} />
          </span>
        </div>

        {/* チケット名 */}
        <h2 css={styles.ticketName}>{ticket.ticketPlanName}</h2>

        {/* 残り枚数 */}
        <div css={styles.remainingSection}>
          <span css={styles.remainingLabel}>
            残り{ticket.unitSuffix || '枚'}数
          </span>
          <span css={styles.remainingCountGroup}>
            <span css={styles.remainingCount(primaryColor)}>
              {ticket.remainingCount}
            </span>
            <span css={styles.remainingUnitContainer}>
              <span css={styles.remainingUnit}>
                {ticket.unitSuffix || '枚'}
              </span>
            </span>
          </span>
        </div>

        {/* 価格情報 */}
        {ticket.price && (
          <>
            <div css={styles.priceInfo}>
              全{ticket.totalCount}回/
              {ticket.price.toLocaleString()}円(税込)×1セット
            </div>
            <div css={styles.totalPrice}>
              {ticket.price.toLocaleString()}
              <span css={styles.totalPriceLabel}>円(税込)</span>
            </div>
          </>
        )}
      </div>

      {/* キャンセルポリシー */}
      {ticket.cancelPolicy && (
        <div>
          <h3 css={styles.policyTitle}>キャンセルポリシー</h3>
          <div css={styles.policyContent}>
            <div css={styles.policyLine}>{ticket.cancelPolicy}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: css`
    min-height: 100vh;
    padding: 0 20px;
  `,
  emptyState: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #666;
    font-size: 14px;
  `,
  ticketCard: css`
    background-color: #ffffff;
    padding: 16px;
    margin-bottom: 24px;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-top: none;
  `,
  ticketTypeSection: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  ticketType: (primaryColor: string) => css`
    background-color: ${primaryColor};
    color: white;
    width: 100%;
    border: 1px solid ${primaryColor};
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 4px 0;
    display: flex;
    justify-content: center;
  `,
  ticketInfoSection: css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  `,
  expiryDate: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
  expiryDateText: css`
    color: #9e9e9e;
    font-size: 10px;
  `,
  expiryDateSuccess: css`
    color: #4caf50;
    font-size: 10px;
  `,
  ticketName: css`
    font-size: 12px;
    font-weight: 500;
    color: #212121;
    line-height: 1.4;
    padding: 0;
  `,
  remainingSection: css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 24px 0 16px 0;
    border-bottom: 1px solid #e0e0e0;
  `,
  remainingCountGroup: css`
    display: flex;
    align-items: center;
  `,
  remainingLabel: css`
    font-size: 16px;
    color: #212121;
    font-weight: bold;
    margin-right: 8px;
  `,
  remainingCount: (primaryColor: string) => css`
    font-size: 32px;
    font-weight: bold;
    color: ${primaryColor};
  `,
  remainingUnitContainer: css`
    margin-left: 4px;
    margin-top: 8px;
  `,
  remainingUnit: css`
    font-size: 16px;
    color: #8d8d8d;
  `,
  priceInfo: css`
    display: flex;
    justify-content: flex-end;
    font-size: 10px;
    line-height: 1.4;
    letter-spacing: 0.8px;
    color: #8d8d8d;
    margin-bottom: 4px;
  `,
  totalPrice: css`
    font-size: 16px;
    font-weight: 500;
    color: #212121;
    text-align: right;
  `,
  totalPriceLabel: css`
    font-size: 12px;
    padding-left: 4px;
  `,
  policyTitle: css`
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  `,
  policyContent: css`
    font-size: 13px;
    color: #666;
    line-height: 1.6;
  `,
  policyLine: css`
    margin-bottom: 4px;
    &:last-child {
      margin-bottom: 0;
    }
  `,
};
